/**
 * 1stbuddha — search.js
 *
 * <search-page> (sections/search.liquid)
 *   - clear button empties the field, hides itself and refocuses the field
 *   - collections: the storefront search doesn't return collections, so they
 *     are fetched from the Predictive Search API (/search/suggest.json) and
 *     rendered from the <template>; the "Collections" link and count appear
 *     when there are any
 *   - result-type links follow the scroll position (aria-current)
 */
(() => {
  if (customElements.get('search-page')) return;

  class SearchPage extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.clear = this.querySelector('[data-search-clear]');

      this.input?.addEventListener('input', () => {
        if (this.clear) this.clear.hidden = this.input.value === '';
      });
      this.clear?.addEventListener('click', () => {
        this.input.value = '';
        this.clear.hidden = true;
        this.input.focus();
      });

      if (this.dataset.terms) this.loadCollections();
      this.spy();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async loadCollections() {
      const section = this.querySelector('[data-collections]');
      const list = this.querySelector('[data-collections-list]');
      const template = this.querySelector('[data-collection-template]');
      if (!section || !list || !template) return;

      const params = new URLSearchParams({
        q: this.dataset.terms,
        'resources[type]': 'collection',
        'resources[limit]': '6',
      });
      try {
        const response = await fetch(`${this.dataset.suggestUrl}.json?${params}`);
        if (!response.ok) return;
        const data = await response.json();
        const collections = data?.resources?.results?.collections || [];
        if (!collections.length) return;

        collections.forEach((collection) => {
          const item = template.content.firstElementChild.cloneNode(true);
          const link = item.querySelector('a');
          link.href = collection.url;
          item.querySelector('.search-collection__title').textContent = collection.title.split(' — ')[0];
          const imageUrl = collection.featured_image?.url || collection.image;
          if (imageUrl) {
            const image = document.createElement('img');
            image.src = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}width=200`;
            image.alt = '';
            image.loading = 'lazy';
            image.width = 200;
            image.height = 200;
            item.querySelector('.search-collection__media').append(image);
          }
          list.append(item);
        });

        section.hidden = false;
        const tab = this.querySelector('[data-collections-tab]');
        const count = this.querySelector('[data-collections-count]');
        if (count) count.textContent = String(collections.length);
        if (tab) tab.hidden = false;
        this.spy();
      } catch {
        /* collections are a bonus; the page works without them */
      }
    }

    spy() {
      this.observer?.disconnect();
      const tabs = [...this.querySelectorAll('[data-search-tabs] a:not([hidden])')];
      const panels = tabs.map((tab) => this.querySelector(tab.getAttribute('href'))).filter(Boolean);
      if (!panels.length || !('IntersectionObserver' in window)) return;

      const setCurrent = (id) =>
        tabs.forEach((tab) => {
          if (tab.getAttribute('href') === `#${id}`) tab.setAttribute('aria-current', 'true');
          else tab.removeAttribute('aria-current');
        });

      this.observer = new IntersectionObserver(
        (entries) => {
          const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible[0]) setCurrent(visible[0].target.id);
        },
        { rootMargin: '-30% 0px -60% 0px' }
      );
      panels.forEach((panel) => this.observer.observe(panel));
    }
  }

  customElements.define('search-page', SearchPage);
})();
